import { useState } from 'react';
import { Button, Card, CardContent, CardTitle } from '@tark/shared/components';
import {
  useClearScheduleAttendance,
  useMarkScheduleBookedRosterPresent,
  useScheduleOccurrenceDetail,
  useUpdateScheduleAttendance,
} from '@tark/shared/hooks';
import type { TenantSessionRosterEntry } from '@tark/shared/types';
import { ListChecks, Loader2 } from 'lucide-react';
import SessionRecapEditor, { type SessionRecapMode } from './SessionRecapEditor';
import SessionPaymentRow from './SessionPaymentRow';

const ATTENDANCE_STATUSES = ['PRESENT', 'LATE', 'ABSENT', 'EXCUSED'];

type SessionAttendancePanelProps = {
  occurrenceId: number;
  emptyRosterMessage?: string;
};

export default function SessionAttendancePanel({
  occurrenceId,
  emptyRosterMessage = 'Add students before marking attendance.',
}: SessionAttendancePanelProps) {
  const { data, isLoading, isError, refetch } = useScheduleOccurrenceDetail(occurrenceId, true);
  const updateAttendance = useUpdateScheduleAttendance();
  const clearAttendance = useClearScheduleAttendance();
  const markBookedRosterPresent = useMarkScheduleBookedRosterPresent();
  const [attendanceActionBookingId, setAttendanceActionBookingId] = useState<number | null>(null);
  const [recapEditor, setRecapEditor] = useState<{ bookingId: number; mode: SessionRecapMode } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const roster = data?.roster.filter((entry) => (entry.bookingStatus || '').toUpperCase() !== 'CANCELLED') ?? [];
  const bookedRoster = roster.filter((entry) => (entry.bookingStatus || '').toUpperCase() === 'BOOKED');

  const updateAttendanceStatus = async (entry: TenantSessionRosterEntry, attendanceStatus: string) => {
    setAttendanceActionBookingId(entry.bookingId);
    setFormError(null);
    try {
      await updateAttendance.mutateAsync({
        occurrenceId,
        bookingId: entry.bookingId,
        payload: { attendanceStatus },
      });
      await refetch();
    } catch {
      setFormError('Could not update attendance.');
    } finally {
      setAttendanceActionBookingId(null);
    }
  };

  const clearAttendanceStatus = async (entry: TenantSessionRosterEntry) => {
    setAttendanceActionBookingId(entry.bookingId);
    setFormError(null);
    try {
      await clearAttendance.mutateAsync({ occurrenceId, bookingId: entry.bookingId });
      await refetch();
    } catch {
      setFormError('Could not clear attendance.');
    } finally {
      setAttendanceActionBookingId(null);
    }
  };

  const markAllBookedPresent = async () => {
    setFormError(null);
    try {
      await markBookedRosterPresent.mutateAsync(occurrenceId);
      await refetch();
    } catch {
      setFormError('Could not mark the booked roster present.');
    }
  };

  const openRecapEditor = (entry: TenantSessionRosterEntry, mode: SessionRecapMode) => {
    if (!entry.attendanceStatus) {
      setFormError('Mark attendance before opening a recap.');
      return;
    }
    setFormError(null);
    setRecapEditor({ bookingId: entry.bookingId, mode });
  };

  return (
    <Card variant="default" padding="none" className="pro-attendance overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{data?.occurrence.title || 'Check-in'}</CardTitle>
          </div>
          {data ? (
            <p className="mt-1 text-sm text-text-muted">
              {formatDateRange(data.occurrence.startsAt, data.occurrence.endsAt)}
            </p>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="outline"
          isLoading={markBookedRosterPresent.isPending}
          disabled={!data || bookedRoster.length === 0}
          onClick={markAllBookedPresent}
        >
          All present
        </Button>
      </div>

      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-surface-elevated/30 px-4 py-3">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <p className="text-sm text-text-muted">Loading roster...</p>
          </div>
        ) : null}

        {isError ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-medium text-amber-700">Roster unavailable right now.</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : null}

        {formError ? <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{formError}</p> : null}

        {data ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <MiniStat label="Booked" value={data.rosterSummary.bookedCount} />
              <MiniStat label="Present" value={data.attendanceSummary.presentCount} />
              <MiniStat label="Unmarked" value={data.attendanceSummary.unmarkedCount} />
            </div>

            {roster.length > 0 ? (
              <div className="space-y-2">
                {roster.map((entry) => (
                  <div key={entry.bookingId} className="rounded-xl border border-border bg-surface px-4 py-3">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-text">{entry.athleteDisplayName}</p>
                          <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${rosterStatusClassName(entry.bookingStatus)}`}>
                            {formatCodeLabel(entry.bookingStatus)}
                          </span>
                          <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${attendanceStatusClassName(entry.attendanceStatus)}`}>
                            {entry.attendanceStatus ? formatCodeLabel(entry.attendanceStatus) : 'Unmarked'}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-text-muted">
                          {[entry.guardianDisplayName ? `Guardian: ${entry.guardianDisplayName}` : null, formatAttendanceTiming(entry)]
                            .filter(Boolean)
                            .join(' / ') || 'Ready for check-in'}
                        </p>
                      </div>
                      {(entry.bookingStatus || '').toUpperCase() === 'BOOKED' ? (
                        <div className="flex flex-wrap gap-1.5 lg:justify-end">
                          <select aria-label={`Attendance for ${entry.athleteDisplayName}`} className="min-h-[44px] max-w-full rounded-lg border border-border bg-surface px-3 text-xs text-text"
                            value={entry.attendanceStatus || ''} disabled={attendanceActionBookingId === entry.bookingId && updateAttendance.isPending}
                            onChange={e=>{if(e.target.value==='FINISH')openRecapEditor(entry,'finish');else if(e.target.value==='RECHECKIN')void updateAttendanceStatus(entry,entry.attendanceStatus!);else if(e.target.value)void updateAttendanceStatus(entry,e.target.value);}}>
                            <option value="" disabled>Unmarked</option>{ATTENDANCE_STATUSES.map(status=><option key={status} value={status}>{formatCodeLabel(status)}</option>)}
                            {isOnCourt(entry) && <option value="FINISH">Finish & recap</option>}
                            {entry.checkedOutAt && isPresentOrLate(entry.attendanceStatus) && <option value="RECHECKIN">Check in again</option>}
                          </select>
                          {entry.attendanceStatus ? (
                            <button
                              type="button"
                              disabled={attendanceActionBookingId === entry.bookingId && clearAttendance.isPending}
                              onClick={() => clearAttendanceStatus(entry)}
                              className="rounded-full border border-border bg-surface px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-subtle transition-colors hover:border-primary/30 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              Clear
                            </button>
                          ) : null}
                          {entry.attendanceStatus && !isOnCourt(entry) ? (
                            <Button size="sm" variant="ghost" onClick={() => openRecapEditor(entry, 'edit')}>
                              Session recap
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    {entry.progressSummary && recapEditor?.bookingId !== entry.bookingId ? (
                      <div className="mt-3 rounded-xl border border-primary/10 bg-primary/5 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">Session note</p>
                        <p className="mt-1 text-sm leading-6 text-text">{entry.progressSummary}</p>
                      </div>
                    ) : null}

                    <SessionPaymentRow occurrenceId={occurrenceId} entry={entry} />

                    {recapEditor?.bookingId === entry.bookingId ? (
                      <SessionRecapEditor
                        occurrenceId={occurrenceId}
                        entry={entry}
                        mode={recapEditor.mode}
                        onClose={() => setRecapEditor(null)}
                        onSaved={() => void refetch()}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-border bg-surface-elevated/20 px-4 py-8 text-center">
                <p className="font-semibold text-text">No roster yet</p>
                <p className="mt-1 text-sm text-text-muted">{emptyRosterMessage}</p>
              </div>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value }: { label: number | string; value: number | string }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">{label}</p>
      <p className="mt-1 text-xl font-semibold leading-none text-text">{value}</p>
    </div>
  );
}

function formatDateRange(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const day = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const startTime = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const endTime = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} / ${startTime} - ${endTime}`;
}

function formatShortDateTime(value?: string | null): string {
  if (!value) {
    return 'Not scheduled';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Not scheduled';
  }
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function isPresentOrLate(status?: string | null): boolean {
  const normalized = (status || '').toUpperCase();
  return normalized === 'PRESENT' || normalized === 'LATE';
}

function isOnCourt(entry: TenantSessionRosterEntry): boolean {
  return isPresentOrLate(entry.attendanceStatus) && Boolean(entry.checkedInAt) && !entry.checkedOutAt;
}

function formatAttendanceTiming(entry: TenantSessionRosterEntry): string | null {
  if (!entry.checkedInAt) {
    return null;
  }
  if (!entry.checkedOutAt) {
    return `Checked in ${formatShortDateTime(entry.checkedInAt)}`;
  }
  const duration = formatAttendanceDuration(entry.checkedInAt, entry.checkedOutAt);
  return `Finished${duration ? ` after ${duration}` : ''}`;
}

function formatAttendanceDuration(checkedInAt: string, checkedOutAt: string): string | null {
  const start = new Date(checkedInAt).getTime();
  const end = new Date(checkedOutAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return null;
  }
  const totalMinutes = Math.max(1, Math.round((end - start) / 60_000));
  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
}

function formatCodeLabel(value?: string | null, fallback = '-'): string {
  if (!value) {
    return fallback;
  }
  return value
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function attendanceStatusClassName(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'PRESENT':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'LATE':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    case 'ABSENT':
      return 'border-red-200 bg-red-50 text-red-700';
    case 'EXCUSED':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    default:
      return 'border-border bg-surface-elevated text-text-subtle';
  }
}

function rosterStatusClassName(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'BOOKED':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'WAITLISTED':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'REQUESTED':
    case 'TRIAL_REQUESTED':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    default:
      return 'border-border bg-surface-elevated text-text-subtle';
  }
}
